import { useCallback, useEffect, useState } from "react";
import { BookOpen, Download, RefreshCw, RotateCcw, Upload } from "lucide-react";
import { PRODUCT_VERSION } from "../../shared/version.js";
import type { Session } from "../../shared/contracts.js";
import { HELP_PACKAGE_MAX_BYTES, type ActiveHelpPackage, type HelpPackage, type HelpPackageList } from "../../shared/help.js";
import { api, ApiError, downloadText } from "../api.js";
import "./help.css";

export default function HelpPackageAdmin({ session, onHelp }: { session?: Session; onHelp?: (topic: string) => void }) {
  const [state, setState] = useState<HelpPackageList | null>(null);
  const [candidate, setCandidate] = useState<unknown>(null);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const canConfigure = !session || session.permissions.includes("studio.configure");
  const load = useCallback(async () => {
    try { setState(await api<HelpPackageList>("/administration/help-packages")); }
    catch (e) { setError(e instanceof Error ? e.message : "Help packages could not be loaded."); }
  }, []);
  useEffect(() => { if (canConfigure) void load(); }, [load, canConfigure]);
  function describeError(e: unknown) {
    const details = e instanceof ApiError && Array.isArray(e.details)
      ? e.details.map((issue: any) => `${issue.path || "Package"}: ${issue.message}`).join("; ") : "";
    return `${e instanceof Error ? e.message : "The Help package operation failed."}${details ? " " + details : ""}`;
  }
  async function change(path: string, message: string, content?: unknown) {
    setBusy(true); setError(""); setNotice("");
    try {
      setState(await api<HelpPackageList>(path, { method: "POST", body: JSON.stringify(content ?? {}) }));
      setNotice(message);
      window.dispatchEvent(new Event("dataglow-help-changed"));
      if (content !== undefined) { setCandidate(null); setFilename(""); }
    } catch (e) { setError(describeError(e)); }
    finally { setBusy(false); }
  }
  async function selectFile(file?: File) {
    setCandidate(null); setFilename(""); setError(""); setNotice("");
    if (!file) return;
    if (file.size > HELP_PACKAGE_MAX_BYTES) { setError("Choose a JSON Help package no larger than 1 MiB."); return; }
    try { setCandidate(JSON.parse(await file.text())); setFilename(file.name); }
    catch { setError("The selected file is not valid JSON."); }
  }
  async function downloadCurrent() {
    setError("");
    try {
      const { manifest, index, contextMap, articles } = await api<ActiveHelpPackage>("/help/package");
      downloadText(`${manifest.packageId}-revision-${manifest.revision}.json`, JSON.stringify({ manifest, index, contextMap, articles }, null, 2));
    } catch (e) { setError(describeError(e)); }
  }
  if (!canConfigure) return <p>Help package administration requires studio.configure permission.</p>;
  const preview = candidate && typeof candidate === "object" ? candidate as Partial<HelpPackage> : null;
  return <section className="dgx-help-admin" data-testid="help-package-admin">
    <div className="dgx-help-admin-heading">
      <div><h3><BookOpen size={18} /> Help packages</h3><p>Install compatible content for this organization, then activate a reviewed revision.</p></div>
      {onHelp && <button className="dgx-link" onClick={() => onHelp("help-packages")}>Help</button>}
    </div>
    <p className="dgx-help-notice">Packages contain plain text, a topic index and context mappings. Product compatibility must be exactly {PRODUCT_VERSION}. Changes apply without rebuilding the application.</p>
    {error && <p role="alert" className="dgx-help-admin-error">{error}</p>}
    {notice && <p role="status" className="dgx-help-admin-success">{notice}</p>}
    {state?.fallbackReason && <p role="status" className="dgx-help-notice">{state.fallbackReason}</p>}
    <div className="dgx-help-admin-actions">
      <button type="button" onClick={() => void downloadCurrent()} disabled={busy}><Download size={15} /> Download active package</button>
      <button type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={15} /> Refresh</button>
      <button type="button" disabled={busy || !state?.previousId} onClick={() => void change("/administration/help-packages/rollback", "Previous compatible Help revision restored.")}><RotateCcw size={15} /> Roll back</button>
    </div>
    <label className="dgx-help-upload"><span><Upload size={16} /> Choose Help JSON package (maximum 1 MiB)</span><input aria-label="Choose Help JSON package" type="file" accept="application/json,.json" disabled={busy} onChange={(e) => { void selectFile(e.target.files?.[0]); e.target.value = ""; }} /></label>
    {candidate !== null && <div className="dgx-help-package-preview">
      <strong>{filename}</strong>
      <p>{typeof preview?.manifest?.title === "string" ? preview.manifest.title : "Package awaiting validation"} · {typeof preview?.manifest?.productVersion === "string" ? `Product ${preview.manifest.productVersion}` : "Product version not specified"} · {typeof preview?.manifest?.revision === "string" ? `Revision ${preview.manifest.revision}` : "Revision not specified"}</p>
      <button type="button" disabled={busy} onClick={() => void change("/administration/help-packages", "Package installed. Choose Activate to make it the organization guide.", candidate)}>Install and validate</button>
    </div>}
    <div className="dgx-help-package-list">
      {state?.packages.map((pkg) => <article key={pkg.id} className="dgx-help-package-row">
        <div><strong>{pkg.manifest.title}</strong><p>Help revision {pkg.manifest.revision} · Product {pkg.manifest.productVersion} · {pkg.source === "bundled" ? "Bundled" : "Installed"}</p><small>{pkg.id}</small></div>
        {state.activeId === pkg.id ? <span className="dgx-help-package-active">Active</span> : <button type="button" disabled={busy} onClick={() => void change(`/administration/help-packages/${encodeURIComponent(pkg.id)}/activate`, `Help revision ${pkg.manifest.revision} activated.`)}>Activate</button>}
      </article>)}
      {!state && !error && <p role="status">Loading Help packages…</p>}
    </div>
    <p className="dgx-help-compatibility">Installing leaves the active guide unchanged. Activation and rollback are recorded in the organization audit log.</p>
  </section>;
}
